/**
 * Monero unit conversion utilities for Cashero.
 *
 * Internally, all amounts are stored and communicated in piconero (atomic units).
 * 1 XMR = 1,000,000,000,000 piconero (10^12).
 *
 * The UI toggles between XMR and piconero display with automatic conversion.
 */

/** 1 XMR = 10^12 piconero */
export const PICONERO_PER_XMR = 1_000_000_000_000;

/** Number of decimal places in XMR */
export const XMR_DECIMALS = 12;

/**
 * Convert XMR (as a decimal string) to piconero (integer).
 *
 * @param xmr - XMR amount as a string (e.g., "0.5", "1", "0.001")
 * @returns Piconero amount as an integer
 *
 * @example
 * xmrToPiconero("0.5") // => 500000000000
 * xmrToPiconero("1")   // => 1000000000000
 * xmrToPiconero("0.001") // => 1000000000
 */
export function xmrToPiconero(xmr: string): number {
  // Handle empty/whitespace
  if (!xmr || xmr.trim() === '') return 0;

  const cleaned = xmr.trim();
  const parts = cleaned.split('.');

  if (parts.length > 2) throw new Error(`Invalid XMR format: ${xmr}`);

  const whole = parts[0] || '0';
  const fraction = parts[1] || '';

  // Remove leading zeros from whole part
  const wholeBig = whole.replace(/^0+/, '') || '0';

  // Pad or truncate fraction to 12 digits
  const fractionPadded = fraction.padEnd(XMR_DECIMALS, '0').slice(0, XMR_DECIMALS);

  const result = BigInt(wholeBig) * BigInt(PICONERO_PER_XMR) + BigInt(fractionPadded || '0');

  // Guard against Number overflow (safe up to ~2^53 / 10^12 ≈ 9000 XMR)
  if (result > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error(`Amount too large: ${xmr} XMR exceeds safe integer range`);
  }

  return Number(result);
}

/**
 * Convert piconero (integer) to XMR (decimal string).
 *
 * @param piconero - Amount in piconero
 * @returns XMR amount as a string (no trailing zeros, up to 12 decimal places)
 *
 * @example
 * piconeroToXmr(500000000000) // => "0.5"
 * piconeroToXmr(1000000000000) // => "1"
 * piconeroToXmr(123456789012) // => "0.123456789012"
 * piconeroToXmr(0) // => "0"
 */
export function piconeroToXmr(piconero: number): string {
  if (piconero === 0) return '0';

  const whole = Math.floor(piconero / PICONERO_PER_XMR);
  const fraction = piconero % PICONERO_PER_XMR;

  if (fraction === 0) return whole.toString();

  const fractionStr = fraction.toString().padStart(XMR_DECIMALS, '0').replace(/0+$/, '');
  return `${whole}.${fractionStr}`;
}

/**
 * Format a piconero amount for display with the specified unit.
 *
 * @param amount - Amount in piconero
 * @param unit - Display unit ('xmr' or 'piconero')
 * @returns Formatted string with unit suffix
 *
 * @example
 * formatAmount(500000000000, 'xmr') // => "0.5 XMR"
 * formatAmount(500000000000, 'piconero') // => "500,000,000,000 piconero"
 */
export function formatAmount(amount: number, unit: 'xmr' | 'piconero'): string {
  if (unit === 'piconero') {
    return `${amount.toLocaleString()} piconero`;
  }
  return `${piconeroToXmr(amount)} XMR`;
}

/**
 * Format a piconero amount as XMR with a specified number of decimal places.
 *
 * @param amount - Amount in piconero
 * @param decimals - Number of decimal places (default: 4)
 * @returns Formatted XMR string
 *
 * @example
 * formatXmr(500000000000) // => "0.5000"
 * formatXmr(1234567890123, 6) // => "1.234568"
 */
export function formatXmr(amount: number, decimals: number = 4): string {
  const xmr = (amount / PICONERO_PER_XMR).toFixed(decimals);
  return xmr;
}

/**
 * Parse a user input string in either XMR or piconero format to piconero.
 *
 * @param input - User input string
 * @param unit - Current display unit of the input
 * @returns Piconero amount
 *
 * @example
 * parseUserInput("0.5", "xmr") // => 500000000000
 * parseUserInput("5000", "piconero") // => 5000
 */
export function parseUserInput(input: string, unit: 'xmr' | 'piconero'): number {
  if (!input || input.trim() === '') return 0;

  if (unit === 'xmr') {
    return xmrToPiconero(input);
  }

  // Piconero — parse as integer
  const cleaned = input.trim().replace(/,/g, '');
  const num = parseInt(cleaned, 10);
  if (isNaN(num) || num < 0) throw new Error(`Invalid piconero amount: ${input}`);
  return num;
}
